import { useCallback,useState } from 'react';
import { useIsTutorialMode } from '@context/PatientDataContext';
import { useAccessToken } from '@features/user';
import APIService from '@services/APIService';
import { validateDob,validateEmail, validatePhone } from '@utils/patientValidation';
import { useSnackbar } from 'notistack';

export interface PatientFieldUpdateOptions {
  patientId: string;
  onUpdate?: (field: string, value: string) => void;
  onError?: (field: string, error: string) => void;
  showSuccessNotification?: boolean;
}

export const usePatientFieldUpdate = ({ patientId, onUpdate, onError, showSuccessNotification = true }: PatientFieldUpdateOptions) => {
  const { enqueueSnackbar } = useSnackbar();
  const { getAccessToken } = useAccessToken();
  const isTutorialMode = useIsTutorialMode();
  const [isUpdating, setIsUpdating] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string | null>>({});

  const validateField = useCallback((field: string, value: string): boolean => {
    let validation: { isValid: boolean; error?: string };
    
    switch (field) {
      case 'mobile_phone':
      case 'phone':
        validation = validatePhone(value);
        break;
      case 'email':
        validation = validateEmail(value);
        break;
      case 'dob':
        validation = validateDob(value);
        break;
      default:
        validation = { isValid: true };
    }

    if (!validation.isValid && validation.error) {
      setFieldErrors(prev => ({ ...prev, [field]: validation.error || null }));
      onError?.(field, validation.error);
      return false;
    }

    setFieldErrors(prev => ({ ...prev, [field]: null }));
    return true;
  }, [onError]);

  const updatePatientField = useCallback(async (
    field: string,
    value: string,
    currentValue?: string
  ): Promise<boolean> => {
    // Skip if value hasn't changed
    if (value === currentValue) return true;

    // Validate the field
    if (!validateField(field, value)) return false;

    // Tutorial mode: pretend the update succeeded; the fetch-layer guard
    // would also short-circuit, but this avoids the round-trip + spinner.
    if (isTutorialMode) {
      onUpdate?.(field, value);
      return true;
    }

    setIsUpdating(true);
    try {
      const accessToken = await getAccessToken();
      
      // Map field names to API expectations
      const apiField = field === 'mobile_phone' ? 'mobile_phone' : field;
      
      // Format phone numbers to E.164 without spaces for the backend
      let formattedValue = value;
      if (field === 'mobile_phone' || field === 'phone') {
        formattedValue = value.replace(/\s+/g, ''); // Remove all spaces
      }
      
      const response = await APIService.makeAPIPostRequest({
        requestString: '/patient/update',
        accessToken,
        body: {
          patient_id: patientId,
          updated_fields: { [apiField]: formattedValue },
        },
      });

      if (response.ok) {
        if (showSuccessNotification) {
          const fieldDisplayName = field === 'mobile_phone' || field === 'phone' 
            ? 'Phone number'
            : field === 'email' 
            ? 'Email address'
            : field === 'dob'
            ? 'Date of birth'
            : field;
            
          enqueueSnackbar(`${fieldDisplayName} saved`, { variant: 'success' });
        }
        onUpdate?.(field, value);
        return true;
      } else {
        throw new Error(response.error?.message || `Failed to update ${field}`);
      }
    } catch (error) {
      console.error(`Error updating ${field}:`, error);
      const errorMessage = error instanceof Error ? error.message : `Failed to update ${field}`;
      enqueueSnackbar(errorMessage, { variant: 'error' });
      onError?.(field, errorMessage);
      return false;
    } finally {
      setIsUpdating(false);
    }
  }, [patientId, getAccessToken, enqueueSnackbar, validateField, onUpdate, onError, showSuccessNotification, isTutorialMode]);

  const clearFieldError = useCallback((field: string) => {
    setFieldErrors(prev => ({ ...prev, [field]: null }));
  }, []);

  return {
    updatePatientField,
    validateField,
    clearFieldError,
    isUpdating,
    fieldErrors,
  };
};